import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { waInboxApi } from '@/lib/api';
import {
  ChatCircle, PaperPlaneTilt, Robot, User, ArrowsClockwise,
  CheckCircle, HandWaving, CaretLeft, Check, Checks, WarningCircle, ArrowClockwise,
} from '@phosphor-icons/react';

const FILTERS = [
  { key: 'all', label: 'All', params: {} },
  { key: 'agent', label: 'Needs agent', params: { mode: 'agent', status: 'open' } },
  { key: 'open', label: 'Open', params: { status: 'open' } },
];

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

export const WhatsAppInbox = ({ brandColor = '#002FA7' }) => {
  const [filter, setFilter] = useState('all');
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const scrollRef = useRef(null);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = FILTERS.find((f) => f.key === filter)?.params || {};
      const { data } = await waInboxApi.list(params);
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      // silent — polling
    } finally {
      setLoadingList(false);
    }
  }, [filter]);

  const fetchThread = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await waInboxApi.thread(id);
      setThread(data);
    } catch (e) {
      toast.error('Failed to load conversation');
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  // Poll list + open thread every 5s.
  useEffect(() => {
    const t = setInterval(() => {
      fetchList();
      if (selectedId) fetchThread(selectedId);
    }, 5000);
    return () => clearInterval(t);
  }, [fetchList, fetchThread, selectedId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const openConversation = (id) => {
    setSelectedId(id);
    setThread(null);
    fetchThread(id);
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!text || !selectedId || sending) return;
    setSending(true);
    try {
      await waInboxApi.reply(selectedId, text);
      setReply('');
      await fetchThread(selectedId);
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const [retrying, setRetrying] = useState(null);
  const retryMsg = async (messageId) => {
    if (retrying) return;
    setRetrying(messageId);
    try {
      await waInboxApi.retryMessage(selectedId, messageId);
      toast.success('Message re-sent');
      await fetchThread(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetrying(null);
    }
  };

  const doAction = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      await fetchThread(selectedId);
      fetchList();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const conv = thread?.conversation;

  return (
    <Card className="border shadow-sm rounded-lg overflow-hidden">
      <div className="flex h-[600px]">
        {/* ── Conversation list ── */}
        <div className={`w-full md:w-[320px] border-r border-slate-200 flex flex-col ${selectedId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
                <ChatCircle size={18} weight="bold" style={{ color: brandColor }} /> Inbox
              </h3>
              <Button variant="ghost" size="sm" onClick={fetchList} className="h-7 px-2">
                <ArrowsClockwise size={14} weight="bold" className={loadingList ? 'animate-spin' : ''} />
              </Button>
            </div>
            <div className="flex gap-1">
              {FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full transition ${
                    filter === f.key ? 'text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                  style={filter === f.key ? { backgroundColor: brandColor } : {}}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">No conversations yet.</div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => openConversation(c.id)}
                  className={`w-full text-left px-3 py-3 border-b border-slate-50 hover:bg-slate-50 transition ${
                    selectedId === c.id ? 'bg-slate-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-sm text-slate-800 truncate">
                      {c.contact_name || c.phone}
                    </span>
                    <span className="text-[10px] text-slate-400 shrink-0">{timeAgo(c.last_message_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {c.mode === 'agent' ? (
                      <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1.5 py-0 font-bold gap-0.5">
                        <HandWaving size={10} weight="bold" /> AGENT
                      </Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 text-[9px] px-1.5 py-0 font-bold gap-0.5">
                        <Robot size={10} weight="bold" /> BOT
                      </Badge>
                    )}
                    {c.status === 'closed' && (
                      <Badge className="bg-slate-100 text-slate-400 text-[9px] px-1.5 py-0">closed</Badge>
                    )}
                    {c.unread_count > 0 && (
                      <span className="ml-auto text-[10px] text-white font-bold rounded-full px-1.5 py-0.5"
                        style={{ backgroundColor: brandColor }}>{c.unread_count}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Thread + composer ── */}
        <div className={`flex-1 flex-col ${selectedId ? 'flex' : 'hidden md:flex'}`}>
          {!conv ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              <div className="text-center">
                <ChatCircle size={36} weight="duotone" className="mx-auto mb-2 opacity-50" />
                Select a conversation to view the thread.
              </div>
            </div>
          ) : (
            <>
              {/* header */}
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3">
                <Button variant="ghost" size="sm" className="md:hidden h-8 px-2" onClick={() => setSelectedId(null)}>
                  <CaretLeft size={16} />
                </Button>
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                  style={{ backgroundColor: brandColor }}>
                  {(conv.contact_name || conv.phone || '?').charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-slate-800 truncate">{conv.contact_name || conv.phone}</div>
                  <div className="text-[11px] text-slate-400">{conv.phone} · {conv.mode === 'agent' ? 'Agent handling' : 'Bot handling'}</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  {conv.mode === 'bot' ? (
                    <Button size="sm" variant="outline" className="h-8 text-xs font-bold"
                      onClick={() => doAction(() => waInboxApi.claim(conv.id), 'You took over this chat')}>
                      <HandWaving size={14} weight="bold" className="mr-1" /> Take over
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="h-8 text-xs font-bold"
                      onClick={() => doAction(() => waInboxApi.setMode(conv.id, 'bot'), 'Returned to bot')}>
                      <Robot size={14} weight="bold" className="mr-1" /> Back to bot
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 text-xs text-slate-500"
                    onClick={() => doAction(() => waInboxApi.close(conv.id), 'Conversation closed')}>
                    <CheckCircle size={14} weight="bold" className="mr-1" /> Close
                  </Button>
                </div>
              </div>

              {/* messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 bg-slate-50 flex flex-col gap-2">
                {(thread?.messages || []).map((m) => (
                  <div key={m.id} className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm ${
                      m.direction === 'outbound'
                        ? m.sender === 'agent' ? 'bg-[#dcf8c6] rounded-br-sm' : 'bg-white border border-slate-100 rounded-br-sm'
                        : 'bg-white border border-slate-100 rounded-bl-sm'
                    }`}>
                      {m.direction === 'outbound' && (
                        <div className="text-[9px] font-bold uppercase tracking-wide mb-0.5 opacity-50">
                          {m.sender === 'agent' ? 'Agent' : 'Bot'}
                        </div>
                      )}
                      {m.body}
                      <div className="text-[9px] text-slate-400 mt-0.5 flex items-center justify-end gap-1">
                        <span>{timeAgo(m.created_at)}</span>
                        {m.direction === 'outbound' && m.status === 'sent' && <Check size={11} className="text-slate-400" />}
                        {m.direction === 'outbound' && m.status === 'delivered' && <Checks size={12} className="text-slate-400" />}
                        {m.direction === 'outbound' && m.status === 'read' && <Checks size={12} weight="bold" className="text-sky-500" />}
                        {m.direction === 'outbound' && m.status === 'failed' && (
                          <button
                            onClick={() => retryMsg(m.id)}
                            disabled={retrying === m.id}
                            className="flex items-center gap-0.5 text-red-500 font-bold hover:underline"
                            title="Delivery failed — click to resend"
                          >
                            <WarningCircle size={11} weight="fill" />
                            {retrying === m.id ? 'sending…' : 'failed · retry'}
                            <ArrowClockwise size={10} weight="bold" className={retrying === m.id ? 'animate-spin' : ''} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* composer */}
              <div className="p-3 border-t border-slate-100 flex items-center gap-2">
                <Input
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendReply(); }}
                  placeholder={conv.mode === 'agent' ? 'Reply as agent…' : 'Reply (this takes over from the bot)…'}
                  disabled={sending}
                  className="text-sm"
                />
                <Button onClick={sendReply} disabled={sending || !reply.trim()}
                  style={{ backgroundColor: brandColor }} className="text-white font-bold shrink-0">
                  <PaperPlaneTilt size={16} weight="bold" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
